/**
 * PhotoCropModal — simple pan + zoom cropper for the profile avatar.
 *
 * Design: fixed 260px circular viewport. User drags the image around and
 * uses a slider to zoom in/out. On confirm, we draw the visible crop
 * region onto a 600x600 canvas and return both standard and compressed
 * base64 variants — matching the output shape of useCvEditing's
 * previous in-place compressImage flow.
 */

import { useEffect, useRef, useState } from 'react';
import { Modal } from './ui/Modal';
import { RefreshCw, Check, ZoomIn, ZoomOut } from 'lucide-react';

const VIEWPORT = 260;
const OUTPUT = 600;

interface PhotoCropModalProps {
  open: boolean;
  onClose: () => void;
  imageSrc: string; // original base64
  onConfirm: (standard: string, compressed: string) => void;
}

export function PhotoCropModal({ open, onClose, imageSrc, onConfirm }: PhotoCropModalProps) {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, ox: 0, oy: 0 });
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Reset and measure image on open / source change
  useEffect(() => {
    if (!open || !imageSrc) return;
    const img = new Image();
    img.onload = () => {
      setNatural({ w: img.naturalWidth, h: img.naturalHeight });
      // initial scale: cover the viewport
      const s = Math.max(VIEWPORT / img.naturalWidth, VIEWPORT / img.naturalHeight);
      setScale(s);
      setOffset({ x: 0, y: 0 });
    };
    img.src = imageSrc;
  }, [open, imageSrc]);

  const minScale = natural.w && natural.h
    ? Math.max(VIEWPORT / natural.w, VIEWPORT / natural.h)
    : 0.1;
  const maxScale = minScale * 4;

  const clampOffset = (x: number, y: number, s: number) => {
    const dispW = natural.w * s;
    const dispH = natural.h * s;
    const maxX = Math.max(0, (dispW - VIEWPORT) / 2);
    const maxY = Math.max(0, (dispH - VIEWPORT) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y)),
    };
  };

  const handleMouseDown = (e: React.PointerEvent) => {
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const handleMouseMove = (e: React.PointerEvent) => {
    if (!dragging) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setOffset(clampOffset(dragStart.current.ox + dx, dragStart.current.oy + dy, scale));
  };
  const handleMouseUp = () => setDragging(false);

  const handleScaleChange = (next: number) => {
    const clamped = Math.max(minScale, Math.min(maxScale, next));
    setScale(clamped);
    setOffset((prev) => clampOffset(prev.x, prev.y, clamped));
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    handleScaleChange(scale * (e.deltaY < 0 ? 1.08 : 1 / 1.08));
  };

  const handleConfirm = async () => {
    if (!imgRef.current || !natural.w || !natural.h) return;

    // Compute the visible crop in source coordinates.
    // (offset = center delta in display px, convert to source px via /scale)
    const srcCropSize = VIEWPORT / scale;
    const cx = natural.w / 2 - offset.x / scale;
    const cy = natural.h / 2 - offset.y / scale;
    const sx = cx - srcCropSize / 2;
    const sy = cy - srcCropSize / 2;

    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT;
    canvas.height = OUTPUT;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, OUTPUT, OUTPUT);
    ctx.drawImage(imgRef.current, sx, sy, srcCropSize, srcCropSize, 0, 0, OUTPUT, OUTPUT);
    const standard = canvas.toDataURL('image/jpeg', 0.85);

    const small = document.createElement('canvas');
    small.width = 300;
    small.height = 300;
    const sctx = small.getContext('2d');
    if (!sctx) return;
    sctx.drawImage(canvas, 0, 0, 300, 300);
    const compressed = small.toDataURL('image/jpeg', 0.5);

    onConfirm(standard, compressed);
    onClose();
  };

  const handleReset = () => {
    setScale(minScale);
    setOffset({ x: 0, y: 0 });
  };

  return (
    <Modal open={open} onClose={onClose} title="Cadrer la photo" size="md">
      <div className="flex flex-col items-center gap-5 py-2">
        {/* Viewport with circular mask */}
        <div
          className="relative overflow-hidden rounded-full bg-black/40 cursor-grab active:cursor-grabbing select-none touch-none shadow-[0_0_0_6px_rgba(255,255,255,0.04)]"
          style={{ width: VIEWPORT, height: VIEWPORT }}
          onPointerDown={handleMouseDown}
          onPointerMove={handleMouseMove}
          onPointerUp={handleMouseUp}
          onPointerCancel={handleMouseUp}
          onWheel={handleWheel}
        >
          {imageSrc && (
            <img
              ref={imgRef}
              src={imageSrc}
              alt=""
              draggable={false}
              className="absolute top-1/2 left-1/2 max-w-none pointer-events-none"
              style={{
                width: natural.w,
                height: natural.h,
                transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${scale})`,
                transformOrigin: 'center center',
              }}
            />
          )}
          <div className="pointer-events-none absolute inset-0 rounded-full border-2 border-white/20" />
        </div>

        {/* Zoom controls */}
        <div className="w-full max-w-sm flex items-center gap-3">
          <button
            type="button"
            onClick={() => handleScaleChange(scale / 1.15)}
            className="p-2 rounded-md bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors"
            aria-label="Zoom arrière"
          >
            <ZoomOut size={14} />
          </button>
          <input
            type="range"
            min={minScale}
            max={maxScale}
            step={(maxScale - minScale) / 100}
            value={scale}
            onChange={(e) => handleScaleChange(Number(e.target.value))}
            className="flex-1 accent-blue-500"
          />
          <button
            type="button"
            onClick={() => handleScaleChange(scale * 1.15)}
            className="p-2 rounded-md bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors"
            aria-label="Zoom avant"
          >
            <ZoomIn size={14} />
          </button>
        </div>

        <p className="text-xs text-white/40 text-center">
          Glissez l'image pour la repositionner. Utilisez la molette ou le curseur pour zoomer.
        </p>

        <div className="w-full flex items-center justify-between gap-3 pt-2 border-t border-white/10">
          <button
            type="button"
            onClick={handleReset}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-white/60 hover:text-white hover:bg-white/5 transition-colors"
          >
            <RefreshCw size={13} />
            Réinitialiser
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-xs font-medium text-white/60 hover:text-white hover:bg-white/5 transition-colors"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-bold bg-blue-600 text-white hover:bg-blue-500 transition-colors"
            >
              <Check size={14} />
              Valider
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
